import { useEffect, useRef, useState } from "react";
import { UserRound } from "lucide-react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { detectSellerMobilePlatform } from "@/lib/navigation/seller-app-launch";
import { buildRequestUrl } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import { useImpersonateStore } from "@/shared/services/useImpersonateStore";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { Button, type ButtonVariant } from "@/shared/rd";

const ALLOWED_ROLES: readonly string[] = [ADMIN_ROLES.ADMIN, ADMIN_ROLES.SUPER_ADMIN, ADMIN_ROLES.CUSTOMER_SERVICE];

/**
 * "Impersonate" on the admin design system: the same behaviour as the shared
 * `ImpersonateButton` (role gate, the blank tab opened inside the click, and
 * the seller-app launch form on a phone), drawn with the RD button.
 */
export function ImpersonateAction({
  storeId,
  locale,
  adminRoles,
  portal = "seller",
  variant = "primary",
}: {
  storeId: string;
  locale: string;
  adminRoles?: string[];
  portal?: "seller" | "restaurant";
  variant?: ButtonVariant;
}) {
  const t = useTranslations();
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const { impersonate, isSubmitting, error } = useImpersonateStore(storeId, locale, portal);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const lock = useRef(false);
  const reset = useRef<number | null>(null);
  const roles = adminRoles ?? session?.admin?.roles ?? null;
  const allowed = roles?.some((role) => ALLOWED_ROLES.includes(role)) ?? false;
  const mobilePlatform = detectSellerMobilePlatform({
    userAgent: window.navigator.userAgent,
    maxTouchPoints: window.navigator.maxTouchPoints,
  });

  useEffect(
    () => () => {
      if (reset.current !== null) window.clearTimeout(reset.current);
    },
    [],
  );

  if ((adminRoles === undefined && isSessionLoading) || !allowed) return null;

  const label = t(keys.admin.impersonateSellerAction);
  const pendingLabel = t(keys.admin.impersonateSellerPending);

  if (mobilePlatform && portal === "seller") {
    const launchPath = `${endpoints.admin.storeImpersonationAppLaunch(storeId)}?${new URLSearchParams({
      locale,
      platform: mobilePlatform,
    }).toString()}`;
    return (
      <form
        method="POST"
        action={buildRequestUrl(launchPath)}
        target="_blank"
        style={{ display: "contents" }}
        onSubmit={(event) => {
          if (lock.current) {
            event.preventDefault();
            return;
          }
          lock.current = true;
          setIsFormSubmitting(true);
          reset.current = window.setTimeout(() => {
            lock.current = false;
            setIsFormSubmitting(false);
          }, 2_000);
        }}
      >
        <Button type="submit" variant={variant} size="sm" icon={UserRound} loading={isFormSubmitting} loadingLabel={pendingLabel}>
          {label}
        </Button>
      </form>
    );
  }

  return (
    <>
      <Button variant={variant} size="sm" icon={UserRound} loading={isSubmitting} loadingLabel={pendingLabel} onClick={() => void impersonate()}>
        {label}
      </Button>
      {error ? (
        <span role="alert" style={{ fontSize: 13, color: "var(--rd-red)" }}>
          {error}
        </span>
      ) : null}
    </>
  );
}
